// frontend/src/utils/forecastHorizon.js
//
// Which months a merchant can see and edit a forecast for, and which months of
// sales the forecast grid shows before them.
//
// The engine writes exactly 12 monthly rows per item-location, starting at the
// store's CURRENT month — `forecast_start_month = anchor_date.strftime('%Y-%m')`
// (backend/forecast_engine/service.py). Current month through current + 11 IS
// the horizon; nothing outside it exists to plan in. The forecast grid's month
// columns (components/forecastgrid/ForecastGrid.jsx) draw it.
//
// The anchor is always passed in (useStoreToday) and never sampled here — a module that reads its own clock reads the viewer's, and this
// window belongs to the store's calendar.

/** How many months the engine forecasts ahead, inclusive of the current one. */
export const HORIZON_LENGTH = 12;

/**
 * The 12 month keys of the horizon: the anchor's month through anchor + 11.
 *
 * @param {string} today the store's today as 'YYYY-MM-DD' (useStoreToday).
 *   REQUIRED and never defaulted. '' (zone not resolved yet) returns [], so a
 *   caller renders no months rather than months the engine may not have
 *   written.
 * @returns {string[]} ascending 'YYYY-MM' keys, or [] without an anchor.
 */
export function horizonMonths(today) {
  return monthKeys(today, 0, HORIZON_LENGTH);
}

/**
 * How many whole months of sales the forecast grid's history columns show:
 * the window `ForecastViewSet._history_window` (backend/forecasting/views.py)
 * sums the sales over.
 */
export const HISTORY_LENGTH = 24;

/**
 * The 24 month keys of the sales history: the 24 whole months before the
 * anchor's month, oldest first.
 *
 * @param {string} today the store's today as 'YYYY-MM-DD' (useStoreToday);
 *   '' returns [], as horizonMonths does.
 * @returns {string[]} ascending 'YYYY-MM' keys, or [] without an anchor.
 */
export function historyMonths(today) {
  return monthKeys(today, -HISTORY_LENGTH, HISTORY_LENGTH);
}

// `count` month keys starting `offset` months from the anchor's month.
function monthKeys(today, offset, count) {
  if (!today) return [];
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7)) - 1;
  return Array.from({ length: count }, (_, i) => {
    // Date.UTC normalizes the month overflow (month 12 → next January, -1 →
    // last December) and the result is read back in UTC, so no zone
    // conversion happens in or out — this is calendar arithmetic on the
    // string we were handed.
    const d = new Date(Date.UTC(year, month + offset + i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/** `'YYYY-MM'` → the `ForecastDate` the API stores that month under. */
export function monthToForecastDate(month) {
  return `${month}-01`;
}
